import { useState, useEffect, useRef } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Shield, CheckCircle, Crown, Zap, RefreshCw, Lock } from "lucide-react";
import blueTick from "../assets/blue.png";
import goldBadge from "../assets/gold.png";
import platinumBadge from "../assets/platinum.png";

export default function Shop() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(null);
  const [togglingRenew, setTogglingRenew] = useState(false);
  const [diamondLoading, setDiamondLoading] = useState(null);
  const [paypalFailed, setPaypalFailed] = useState(false);

  const hasActivePremium =
    user?.premium_tier &&
    user?.premium_expires_at &&
    new Date(user.premium_expires_at) > new Date();
  const isVerified = user?.verified;

  const handlePurchase = async (type) => {
    if (type !== "verify" && !isVerified) {
      toast.error("You must be verified before purchasing premium.");
      return;
    }
    setLoading(type);
    try {
      const endpoint =
        type === "verify" ? "/purchase/verify" : `/purchase/premium?tier=${type}`;
      const res = await api.post(endpoint);
      await refresh();
      if (type === "verify") toast.success("You are now verified! 🎉");
      else
        toast.success(
          `Welcome to ${type === "gold" ? "Gold" : "Platinum"}! Unlimited access activated.`,
        );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    } finally {
      setLoading(null);
    }
  };

  const handleToggleAutoRenew = async () => {
    setTogglingRenew(true);
    try {
      const res = await api.put("/premium/auto-renew");
      await refresh();
      toast.success(
        res.data.auto_renew ? "Auto‑renewal enabled" : "Auto‑renewal disabled",
      );
    } catch (err) {
      toast.error("Failed to toggle auto‑renewal");
    } finally {
      setTogglingRenew(false);
    }
  };

  const diamondPackages = [
    { id: "52", diamonds: 52, price: "$3" },
    { id: "120", diamonds: 120, price: "$7" },
    { id: "310", diamonds: 310, price: "$16" },
    { id: "770", diamonds: 770, price: "$32" },
  ];

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // ~10 seconds

    const tryRender = () => {
      if (cancelled) return;

      // If PayPal SDK hasn't loaded yet, poll
      if (!window.paypal) {
        if (++attempts > MAX_ATTEMPTS) {
          console.warn("[Shop] PayPal SDK not available after 10s");
          setPaypalFailed(true);
          return;
        }
        setTimeout(tryRender, 500);
        return;
      }

      setPaypalFailed(false);

      diamondPackages.forEach((pkg) => {
        const containerId = `paypal-container-${pkg.id}`;
        const container = document.getElementById(containerId);
        if (!container) return;
        container.innerHTML = "";

        try {
          window.paypal
            .Buttons({
              style: {
                layout: "horizontal",
                color: "gold",
                shape: "rect",
                label: "paypal",
              },
              createOrder: async () => {
                setDiamondLoading(pkg.id);
                try {
                  const { data } = await api.post(
                    `/diamonds/create-order?package_id=${pkg.id}`,
                  );
                  return data.id;
                } catch (err) {
                  toast.error("Failed to create PayPal order");
                  setDiamondLoading(null);
                  throw err;
                }
              },
              onApprove: async (data) => {
                setDiamondLoading(pkg.id);
                try {
                  await api.post(
                    `/diamonds/capture-order?order_id=${data.orderID}&package_id=${pkg.id}`,
                  );
                  await refresh();
                  toast.success(`You received ${pkg.diamonds} diamonds! 💎`);
                } catch (err) {
                  toast.error(
                    "Payment failed. If you were charged, contact support.",
                  );
                } finally {
                  setDiamondLoading(null);
                }
              },
              onError: (err) => {
                console.error("[PayPal] error:", err);
                toast.error("PayPal error – try again");
                setDiamondLoading(null);
              },
              onCancel: () => {
                toast.info("Payment cancelled");
                setDiamondLoading(null);
              },
            })
            .render(`#${containerId}`);
        } catch (err) {
          console.error("[Shop] PayPal render failed for", pkg.id, err);
        }
      });
    };

    tryRender();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-display text-3xl font-semibold mb-6">Diamond Shop</h1>
        <p className="text-sm text-[#6B6B70] mb-8">
          Spend your diamonds to unlock premium features and support the community.
        </p>

        <div className="bg-white border rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-sm text-gray-600">Your Status</h3>
              {user.verified ? (
                <span className="text-blue-600 text-sm flex items-center gap-1 mt-1">
                  <img src={blueTick} alt="verified" className="w-4 h-4" /> Verified
                </span>
              ) : (
                <span className="text-gray-400 text-sm">Not verified</span>
              )}
              {hasActivePremium ? (
                <span className="text-yellow-600 text-sm flex items-center gap-1 mt-1">
                  <img
                    src={user.premium_tier === "gold" ? goldBadge : platinumBadge}
                    alt={user.premium_tier}
                    className="w-4 h-4"
                  />
                  {user.premium_tier === "gold" ? "Gold" : "Platinum"} active until{" "}
                  {new Date(user.premium_expires_at).toLocaleDateString()}
                </span>
              ) : user.verified ? (
                <span className="text-gray-400 text-sm">No active premium</span>
              ) : null}
            </div>
            {hasActivePremium && (
              <button
                onClick={handleToggleAutoRenew}
                disabled={togglingRenew}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors"
                style={{
                  borderColor: user.auto_renew ? "#16A34A" : "#E7E5E0",
                  color: user.auto_renew ? "#16A34A" : "#6B6B70",
                  backgroundColor: user.auto_renew ? "#F0FDF4" : "#FAFAF7",
                }}
              >
                <RefreshCw className="w-4 h-4" />
                {togglingRenew
                  ? "Updating..."
                  : user.auto_renew
                  ? "Auto‑renew ON"
                  : "Auto‑renew OFF"}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {hasActivePremium
              ? user.auto_renew
                ? "Premium will automatically renew using diamonds when it expires."
                : "Auto‑renewal is off. You can manually renew after expiration."
              : "Verification is permanent. Premium gives you unlimited actions and full gallery access."}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <div className="bg-white border rounded-2xl p-6 shadow-sm flex flex-col">
            <img src={blueTick} alt="verify" className="w-8 h-8 mb-3" />
            <h3 className="font-semibold text-lg">Blue Tick</h3>
            <p className="text-sm text-gray-500 mt-1">
              Get verified and unlock story posting.
            </p>
            <p className="text-2xl font-bold mt-4">💎 10</p>
            <button
              onClick={() => handlePurchase("verify")}
              disabled={loading === "verify" || user.verified}
              className="mt-4 neo-btn bg-blue-600 border-blue-600 w-full disabled:opacity-50"
            >
              {user.verified
                ? "Verified"
                : loading === "verify"
                ? "Buying..."
                : "Get Verified"}
            </button>
          </div>

          <div className="bg-white border rounded-2xl p-6 shadow-sm flex flex-col">
            <img src={goldBadge} alt="gold" className="w-8 h-8 mb-3" />
            <h3 className="font-semibold text-lg">Gold</h3>
            <p className="text-sm text-gray-500 mt-1">
              30 days of unlimited swipes & chat. No restrictions.
            </p>
            <p className="text-2xl font-bold mt-4">💎 39</p>
            {!isVerified ? (
              <button
                disabled
                className="mt-4 neo-btn bg-gray-300 border-gray-300 w-full text-gray-500 cursor-not-allowed"
              >
                <Lock className="w-4 h-4 mr-1 inline" /> Verify first
              </button>
            ) : hasActivePremium ? (
              <button
                disabled
                className="mt-4 neo-btn bg-gray-300 border-gray-300 w-full text-gray-500 cursor-not-allowed"
              >
                Already premium
              </button>
            ) : (
              <button
                onClick={() => handlePurchase("gold")}
                disabled={loading === "gold"}
                className="mt-4 neo-btn bg-yellow-500 border-yellow-500 w-full"
              >
                {loading === "gold" ? "Buying..." : "Get Gold"}
              </button>
            )}
          </div>

          <div className="bg-white border rounded-2xl p-6 shadow-sm flex flex-col">
            <img src={platinumBadge} alt="platinum" className="w-8 h-8 mb-3" />
            <h3 className="font-semibold text-lg">Platinum</h3>
            <p className="text-sm text-gray-500 mt-1">
              6 months of everything unlimited. Best value!
            </p>
            <p className="text-2xl font-bold mt-4">💎 199</p>
            {!isVerified ? (
              <button
                disabled
                className="mt-4 neo-btn bg-gray-300 border-gray-300 w-full text-gray-500 cursor-not-allowed"
              >
                <Lock className="w-4 h-4 mr-1 inline" /> Verify first
              </button>
            ) : hasActivePremium ? (
              <button
                disabled
                className="mt-4 neo-btn bg-gray-300 border-gray-300 w-full text-gray-500 cursor-not-allowed"
              >
                Already premium
              </button>
            ) : (
              <button
                onClick={() => handlePurchase("platinum")}
                disabled={loading === "platinum"}
                className="mt-4 neo-btn bg-purple-600 border-purple-600 w-full"
              >
                {loading === "platinum" ? "Buying..." : "Get Platinum"}
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-gray-400">
          You have 💎 {user.diamonds || 0} diamonds and 🪙 {user.tokens || 0} tokens.
        </div>

        <div className="mt-8 pt-8 border-t border-[#E7E5E0]">
          <h2 className="font-display text-2xl font-semibold mb-4">Buy Diamonds</h2>
          <p className="text-sm text-[#6B6B70] mb-6">
            Purchase diamonds with PayPal. Secure and instant.
          </p>

          {paypalFailed && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              PayPal isn't loading right now. This usually means a content blocker is
              active. Disable it and reload, or contact support.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {diamondPackages.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-white border rounded-2xl p-4 text-center shadow-sm"
              >
                <div className="text-3xl mb-2">💎</div>
                <div className="font-bold text-lg">{pkg.diamonds} Diamonds</div>
                <div className="text-sm text-gray-500 mb-3">{pkg.price}</div>
                <div id={`paypal-container-${pkg.id}`}></div>
                {diamondLoading === pkg.id && (
                  <div className="text-xs text-gray-500 mt-1">Processing...</div>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Payments are processed by PayPal. Diamonds are added instantly after successful
            payment.
          </p>
        </div>
      </main>
    </div>
  );
}